import type {BoardColumn, BoardRow} from '@github-ui/board-core'
import {replaceDateTokens} from '@github-ui/issue-url-helper'
import {usePreviewQueryContext} from '@github-ui/saved-views/contexts/PreviewQueryContext'
import {useCallback, useMemo} from 'react'

import {BoardCard, type BoardFilter} from '../components/board/issues/BoardCard'
import type {IssuesBoardMetadata} from '../components/board/issues/board-metadata'
import type {BoardPayloadItem} from '../components/board/issues/board-payload'
import {IssuesBoardBlankslate} from '../components/board/issues/IssuesBoardBlankslate'
import {BoardColumnHeading} from '../components/board/issues/BoardColumnHeading'
import {BoardRowHeading} from '../components/board/issues/BoardRowHeading'
import {SavedViewBoard} from '../components/board/SavedViewBoard'
import {useFields} from '../contexts/views/FieldsProvider'
import {useVisibleFieldIdentifiers} from '../contexts/views/VisibleFieldsProvider'
import {createRepositoryBoardAdapter} from '../helpers/create-repository-board-adapter'

export function RepositorySavedViewBoard({owner, repo, query}: {owner: string; repo: string; query: string}) {
  const visibleFieldIdentifiers = useVisibleFieldIdentifiers()
  const {fields: fieldDefinitions} = useFields()
  const adapter = useMemo(() => createRepositoryBoardAdapter({owner, repo}), [owner, repo])
  const previewQuery = usePreviewQueryContext()
  // The repository read and edit views provide this, with the same handler their filter bar uses.
  if (!previewQuery) throw new Error('RepositorySavedViewBoard needs a PreviewQueryProvider')
  const {onQueryChange} = previewQuery
  const filter = useMemo<BoardFilter>(() => ({query, apply: onQueryChange}), [onQueryChange, query])
  const renderColumnHeader = useCallback(
    (column: BoardColumn, metadata: IssuesBoardMetadata) => (
      <BoardColumnHeading column={column} labels={metadata.columnLabels} />
    ),
    [],
  )

  const renderRowHeader = useCallback(
    (row: BoardRow, metadata: IssuesBoardMetadata) => <BoardRowHeading row={row} labels={metadata.rowLabels} />,
    [],
  )

  const renderCard = useCallback(
    (item: BoardPayloadItem) => (
      <BoardCard
        item={item}
        visibleFieldIdentifiers={visibleFieldIdentifiers}
        fieldDefinitions={fieldDefinitions}
        filter={filter}
      />
    ),
    [visibleFieldIdentifiers, fieldDefinitions, filter],
  )

  const renderEmptyState = useCallback(
    (metadata: IssuesBoardMetadata) => <IssuesBoardBlankslate query={query} metadata={metadata} />,
    [query],
  )

  return (
    <SavedViewBoard
      adapter={adapter}
      options={{query: replaceDateTokens(query)}}
      renderCard={renderCard}
      renderColumnHeader={renderColumnHeader}
      renderRowHeader={renderRowHeader}
      renderEmptyState={renderEmptyState}
    />
  )
}
